import { useState } from "react";
import { useTranslation } from "react-i18next";
import PaletteIcon from "@/assets/icons/palette.svg?react";
import ColisIcon from "@/assets/icons/colis.svg?react";
import { CircleEllipsis } from "lucide-react";
import BlueCircleIcon from "@/assets/icons/blue-circle-icon.svg?react";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { useCustomQuotationTypeLabels } from "@/hooks/useEnumLabels";

type CustomCotationsTypeSelectionProps = {
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
};

export const CustomCotationsTypeSelection = ({
  setActiveTab,
}: CustomCotationsTypeSelectionProps) => {
  const { t } = useTranslation("boQuotations");
  const typeLabels = useCustomQuotationTypeLabels();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  return (
    <div className="flex flex-1 flex-col items-center gap-6">
      <div className="shadow-basic flex w-full flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6 sm:w-[600px]">
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <div className="flex w-full flex-col justify-center gap-3 sm:flex-row">
            <TypeBtn
              index={0}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              text={typeLabels[CustomQuotationTypeEnum.PALETTE]}
              Icon={PaletteIcon}
            />
            <TypeBtn
              index={1}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              text={typeLabels[CustomQuotationTypeEnum.PARCEL]}
              Icon={ColisIcon}
            />
            <TypeBtn
              index={2}
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              text={typeLabels[CustomQuotationTypeEnum.OTHER]}
              Icon={CircleEllipsis}
            />
          </div>
        </div>
      </div>
      <div className="flex w-full flex-row gap-3 sm:w-fit">
        <button
          onClick={() => setActiveTab(selectedIndex + 1)}
          className="text-normal bg-brand700 hover:bg-brand600 flex-1 px-4 py-2 font-semibold text-white disabled:bg-gray-200 disabled:text-gray-500"
        >
          {t("typeSelection.continue")}
        </button>
      </div>
    </div>
  );
};

type TypeBtnProps = {
  index: number;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  text: string;
  Icon: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
};

const TypeBtn = ({ index, selectedIndex, setSelectedIndex, text, Icon }: TypeBtnProps) => {
  return (
    <button
      className={`flex flex-col items-center justify-center gap-2 rounded-xl border bg-gray-50 ${index === selectedIndex ? "border-brand600" : "border-gray-300"} w-full p-4 focus:outline-hidden sm:w-[150px]`}
      onClick={() => setSelectedIndex(index)}
    >
      <div className="flex w-full flex-row items-start justify-between gap-2 sm:justify-center">
        <div className="h-5 w-5" />
        <div className="flex h-[100px] items-center">
          <Icon className={"h-[40px] w-[60px]"} />
        </div>
        {index === selectedIndex ? (
          <BlueCircleIcon width={20} height={20} />
        ) : (
          <div className="h-5 w-5 rounded-full border border-gray-200 bg-white" />
        )}
      </div>
      <p>{text}</p>
    </button>
  );
};
